import { useEffect, useRef, useState } from "react";
import { useReactToPrint } from "react-to-print";
import LocalPrintshopIcon from "@mui/icons-material/LocalPrintshop";
import axiosInstance from "api/AxiosInstance";
import { Tip, Diamond } from "@utils/defaultImages";
import DietPlanLoader from "@components/ui/Loaders/DietPlanLoader";
import ImageWithFallback from "@components/ui/ImageFallback";
import ButtonLoader from "@components/ui/Loaders/ButtonLoader";
import { toast } from "react-toastify";
import { useLocation, useNavigate } from "react-router-dom";
import { ChevronLeft } from "lucide-react";
import { useTranslation } from "react-i18next";
import { downloadRecipe, gerDietPlanRecipe } from "api/dietPlan";
import ActualDietPlanLoader from "@components/ui/Loaders/ActualDietPlanLoader";
import NotFound from "@components/ui/NotFound";
import RecipeDetailCardView from "./RecipeDetailCardView";
import { getRecipeDetailsData } from "data/uiData/CardsData/getDietPlanData";
import GridCards from "@components/ui/GridCards";

const MealDescriptionModal = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  if (!location.state) {
    navigate(-1);
  }
  const {
    mealType = null,
    index = null,
    day = null,
    dashboard = false,
    mealListing = false,
    allMeals = false,
    tab = null,
    tabCategory = "meals",
    startDate = null,
    dietPlanDate = null,
  } = location.state || {};
  const [meal, setMeal] = useState(null);
  const [mealImage, setMealImage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [descLoading, setDescLoading] = useState(false);
  const [desc, setDesc] = useState(null);
  const [bgImageError, setBgImageError] = useState(false);
  const [firstLoad, setFirstLoad] = useState(true);

  const handleDownloadPdf = async () => {
    try {
      setLoading(true);
      const payload = {
        mealType: mealType?.charAt(0).toUpperCase() + mealType?.slice(1),
        day: day,
        index,
        date: startDate,
      };
      const response = await downloadRecipe(payload);

      const fileBlob = new Blob([response.data], { type: "application/pdf" });
      const downloadUrl = window.URL.createObjectURL(fileBlob);

      const a = document.createElement("a");
      a.href = downloadUrl;
      a.download = `${meal?.MealName}.pdf`;
      document.body.appendChild(a);
      a.click();

      document.body.removeChild(a);
      window.URL.revokeObjectURL(downloadUrl);
      toast.info(t("toast.Recipe downloaded successfully!"));
    } catch (error) {
      console.error("Error downloading file:", error);
      toast.error(t("toast.Failed to download recipe. Please try again."));
    } finally {
      setLoading(false);
    }
  };
  const contentRef = useRef();
  const reactToPrintFn = useReactToPrint({
    contentRef: contentRef,
    documentTitle: `${meal?.meal_name?.[mealType?.toLowerCase()]}_Recipe`,
  });

  const handlePrint = () => {
    const goBackBtn = document.querySelector(".go-back-button");
    try {
      if (goBackBtn) goBackBtn.style.display = "none";
      reactToPrintFn();
    } catch (error) {
      console.log(error);
    } finally {
      if (goBackBtn) goBackBtn.style.display = "";
    }
  };
  const fecthDesc = async (mealName) => {
    setDescLoading(true);
    try {
      const response = await axiosInstance.post("api/meal-instruction", {
        meal_name: mealName,
        day: day,
        mealType: mealType?.charAt(0).toUpperCase() + mealType?.slice(1),
        Ingredients: meal?.Ingredients,
      });

      const data = response.data;

      if (data?.description_html) {
        setDesc(data.description_html);
      } else {
        setDesc(null);
        toast.error(t("No description found for this meal."));
      }
    } catch (error) {
      console.error(error);
      toast.error(t("Failed to fetch meal description. Please try again."));
      setDesc(null);
    } finally {
      setDescLoading(false);
    }
  };

  const fetchSingleMeal = async () => {
    try {
      const data = await gerDietPlanRecipe({
        mealType: mealType?.charAt(0).toUpperCase() + mealType?.slice(1),
        day: day,
        index,
      });
      setMeal(data?.meal);
      setMealImage(data?.meal_image);
      if (data?.meal?.Description) {
        setDesc(data?.meal?.Description);
      } else {
        const mealName = data.meal?.MealName;
        fecthDesc(mealName);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setFirstLoad(false);
    }
  };

  useEffect(() => {
    fetchSingleMeal();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const carbValue = (() => {
    const value = meal?.Nutritions?.Carbohydrates;
    if (!value) return "-";
    return typeof value === "string" && value.trim().toLowerCase().endsWith("g")
      ? value
      : `${value}g`;
  })();

  const proteinValue = (() => {
    const value = meal?.Nutritions?.Protein;
    if (!value) return "-";
    return typeof value === "string" && value.trim().toLowerCase().endsWith("g")
      ? value
      : `${value}g`;
  })();

  const nutrientData = getRecipeDetailsData({
    meal,
    mealType,
    carbValue,
    proteinValue,
    t,
  });
  if (location.state) {
    return (
      <>
        {firstLoad ? (
          <ActualDietPlanLoader />
        ) : (
          <div className="bg-white rounded-[36px] pb-5">
            {meal ? (
              <>
                <div ref={contentRef}>
                  <div className="mb-5">
                    <div className="px-4 py-3">
                      <div
                        className="w-full h-[356px] relative block bg-white rounded-[36px] bg-cover bg-center"
                        style={{
                          backgroundImage: bgImageError
                            ? 'url("/MealOne.png")'
                            : `url(${mealImage})`,
                        }}
                        onError={() => setBgImageError(true)}
                      >
                        <img
                          src={mealImage}
                          alt="image"
                          className="hidden"
                          onError={() => setBgImageError(true)}
                        />
                        <div
                          className="flex items-center gap-3 absolute bg-[linear-gradient(150deg,_#0771EF_12.89%,_#15AAFC_85.22%)] py-2 px-4 rounded-full
                            left-[3%] lg;left-[2%]
                            top-[4%] "
                        >
                          <button
                            onClick={() =>
                              navigate(
                                dashboard
                                  ? "/"
                                  : mealListing || allMeals
                                  ? "/diet-plan"
                                  : -1,
                                {
                                  state: {
                                    tab: tab,
                                    category: tabCategory,
                                    dietPlanDate: dietPlanDate,
                                    day: day,
                                  },
                                }
                              )
                            }
                            className="go-back-button   text-nowrap flex items-center gap-2"
                          >
                            <div className="bg-white text-white rounded-[100px] p-[6px] lg:p-[10px] ">
                              <ChevronLeft
                                className="w-5 h-5 text-[#0575e6] rounded-full "
                                strokeWidth={4}
                              />
                            </div>
                            <p className="font-[Lato] font-[600] text-[16px] text-white">
                              {t("weeklyMealDetails.goBack")}
                            </p>
                          </button>
                        </div>
                      </div>
                    </div>
                    <div className="px-4 md:px-10">
                      <div
                        className="
                          recipe-button mt-1 mb-3 my-3
                          bg-[linear-gradient(150deg,_#0771EF_12.89%,_#15AAFC_85.22%)]
                          text-white rounded-[100px]
                          text-[12px] md:text-[20px]
                          font-[Lato]
                          px-4 md:px-10
                          text-center
                          leading-snug
                          line-clamp-2 md:line-clamp-1
                        "
                        title={meal?.MealName}
                      >
                        <p className="py-3">{meal?.MealName}</p>
                      </div>
                    </div>
                    <GridCards
                      data={nutrientData}
                      className="px-[20px] lg:px-[40px] justify-center grid grid-cols-3 md:grid-cols-6 gap-2 font-[Lato] mt-[20px] mb-[30px]"
                      insideCardClass="py-5 px-3 rounded-[17px] bg-white border border-[#E6E6E6]  rounded-[16px] flex flex-col xl:flex-row items-center gap-2 justify-center"
                      iconClass="rounded-full w-12 h-12 flex items-center justify-center"
                      labelClass="text-[10px] md:text-[12px] font-[500] text-[#6C6A6ACC] text-center xl:text-start"
                      valueClass="text-[10px] md:text-[16px] font-[500] text-[#6C6A6A] text-center xl:text-start whitespace-nowrap overflow-hidden text-ellipsis"
                    />

                    <div
                      className="page-break-desktop"
                      style={{ pageBreakAfter: "always" }}
                    ></div>

                    <div className="px-[20px] lg:px-[40px]">
                      <div className="grid grid-cols-1 lg:grid-cols-5 items-center justify-between  gap-5 bg-[linear-gradient(150deg,_#0771EF_12.89%,_#15AAFC_85.22%)] p-5 text-white rounded-[18px]">
                        <div className="lg:col-span-3">
                          <h2 className="text-[16px] lg:text-[24px] font-[600] font-[Lato]">
                            {t(`recipeDetails.ingredients`)}
                          </h2>
                          <p className="text-[10px] lg:text-[14px] font-[400] font-[Lato] flex items-center gap-2 mb-2">
                            <Tip className="!min-w-6 !h-6" />
                            {t(`recipeDetails.tip`)}: {meal?.Tip}
                          </p>
                          <div className="grid grid-cols-2  xl:grid-cols-3 px-[17px] py-[33px] gap-[17px] border border-[2px] border-[#EBEBEB] rounded-[10px] bg-white text-black">
                            {meal?.Ingredients?.map((item, index) => {
                              return (
                                <p
                                  key={index}
                                  className="text-[10px] lg:text-[14px] font-[400] font-[Lato] flex items-center gap-2"
                                >
                                  <Diamond className="min-w-3 min-h-3 max-w-3 max-h-3" />
                                  {item}
                                </p>
                              );
                            })}
                          </div>
                        </div>
                        <div className="flex justify-end lg:col-span-2">
                          <ImageWithFallback
                            src={mealImage}
                            alt={mealType}
                            width={"100%"}
                            height={"128px"}
                            className="w-full !object-contain max-w-full md:!object-cover rounded-lg xl:col-span-2 min-h-[234px] max-h-234px"
                          />
                        </div>
                      </div>
                      <div
                        style={{
                          pageBreakBefore: "always",
                          breakBefore: "page",
                          height: "0",
                          margin: "0",
                          padding: "0",
                        }}
                      ></div>

                      <div
                        className="page-break mt-[30px] lg:mt-[60px] p-6 rounded-[18px]"
                        style={{
                          background:
                            "linear-gradient(150deg, #0771EF 12.89%, #15AAFC 85.22%)",
                          pageBreakBefore: "always",
                        }}
                      >
                        <div className="flex items-center justify-between mb-3">
                          <h2 className="text-[16px] lg:text-[20px] text-white font-[500] font-[Lato]">
                            {t(`recipeDetails.preparationMethod`)}
                          </h2>
                        </div>
                        {descLoading ? (
                          <DietPlanLoader />
                        ) : desc ? (
                          <RecipeDetailCardView mindfulnessStepsData={desc} />
                        ) : (
                          <p className="text-center text-white mt-4">
                            {t("No meal description available.")}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {!descLoading && (
                  <div className="flex items-center justify-end mb-5 px-5 sm:px-12 gap-3">
                    <button
                      onClick={() => handleDownloadPdf()}
                      disabled={loading}
                      className={`py-[10px] px-[20px] bg-[linear-gradient(150deg,_#0771EF_12.89%,_#15AAFC_85.22%)] text-white rounded-[100px] text-[12px] lg:text-[20px] min-w-[222px] h-[50px]`}
                    >
                      {loading ? (
                        <ButtonLoader />
                      ) : (
                        t(`recipeDetails.downloadRecipe`)
                      )}
                    </button>

                    <button
                      onClick={handlePrint}
                      className=" p-3 rounded-md float-end border border-[3px] border-[#F2F2F2B8] hover:border-[#0575E6] shadow-md"
                    >
                      <LocalPrintshopIcon className="text-[#0575E6]" />
                    </button>
                  </div>
                )}
              </>
            ) : (
              <div className="p-5">
                <NotFound message="No Recipe Found" recipeDetail={true} />
              </div>
            )}
          </div>
        )}
      </>
    );
  }
};

export default MealDescriptionModal;
